import { CartItem } from './cart';
import { IBusinessRule } from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
} from '../constants/brandMenu';
import logger from './logger';
import { IItemConversionInfo } from './itemConversion.types';
import { ConversionSize } from './itemConversion.constants';
import { matchesRulePattern } from './optionReplacement';

/**
 * Extracts the conversion size from a menu item name (e.g., "small combo" -> "small")
 */
const getConversionSizeFromSelection = (
  menuItemName: string
): ConversionSize | null => {
  if (!menuItemName) return null;

  const lowerName = menuItemName.toLowerCase().trim();

  if (lowerName.includes(ConversionSize.Large)) return ConversionSize.Large;
  if (lowerName.includes(ConversionSize.Medium)) return ConversionSize.Medium;
  if (lowerName.includes(ConversionSize.Small)) return ConversionSize.Small;

  return null;
};

/**
 * Finds a matching combo conversion business rule for the given item and constraint
 */
const normalizeOptionValue = (value: string): string =>
  value.toLowerCase().replace(/_/g, ' ').trim();

const normalizeConstraintValue = (value: string): string =>
  value.toLowerCase().trim();

const matchesRuleItems = (
  items: IBusinessRule['items_to_apply'],
  candidate: string
): boolean => {
  return matchesRulePattern(items, [candidate], 'deny', normalizeOptionValue);
};

const matchesRuleConstraints = (
  constraints: IBusinessRule['constraints_to_apply'],
  candidate: string
): boolean => {
  return matchesRulePattern(
    constraints,
    [candidate],
    'allow',
    normalizeConstraintValue
  );
};

const findMatchingComboConversionRule = (
  itemName: string,
  constraintName: string,
  selectedOption: string,
  businessRules: IBusinessRule[]
): IBusinessRule | null => {
  if (!businessRules || businessRules.length === 0) {
    return null;
  }

  return (
    businessRules.find((rule) => {
      // Only match combo conversion rules
      if (rule.category !== BusinessRuleCategory.ComboConversion) {
        return false;
      }

      if (rule.then.operation !== BusinessRuleOption.ConvertToCombo) {
        return false;
      }
      // Check if this rule applies to the item
      if (!matchesRuleItems(rule.items_to_apply, itemName)) {
        return false;
      }

      // Check if this rule applies to the constraint
      if (!matchesRuleConstraints(rule.constraints_to_apply, constraintName)) {
        return false;
      }

      // Check if the selected option matches any of the rule's if.options
      const normalizedSelectedOption = normalizeOptionValue(selectedOption);
      return rule.if.options.some(
        (option) => normalizeOptionValue(option) === normalizedSelectedOption
      );
    }) || null
  );
};

const findMatchingALaCarteConversionRule = (
  itemName: string,
  constraintName: string,
  selectedOption: string,
  businessRules: IBusinessRule[]
): IBusinessRule | null => {
  if (!businessRules || businessRules.length === 0) {
    return null;
  }

  return (
    businessRules.find((rule) => {
      if (rule.category !== BusinessRuleCategory.ALaCarteConversion) {
        return false;
      }

      if (rule.then.operation !== BusinessRuleOption.ConvertToALaCarte) {
        return false;
      }

      if (!matchesRuleItems(rule.items_to_apply, itemName)) {
        return false;
      }

      if (!matchesRuleConstraints(rule.constraints_to_apply, constraintName)) {
        return false;
      }

      const normalizedSelectedOption = normalizeOptionValue(selectedOption);
      return rule.if.options.some(
        (option) => normalizeOptionValue(option) === normalizedSelectedOption
      );
    }) || null
  );
};

/**
 * Analyzes a modifier selection to determine if it should trigger an item conversion
 * Uses business rules from the menu JSON exclusively (no config fallback)
 *
 * @param modifierGroupName - Name of the modifier group
 * @param selectedMenuItemName - Name of the selected menu item
 * @param cartItem - The current cart item being modified
 * @param businessRules - Business rules from menu JSON
 * @returns ItemConversionInfo with conversion details
 */
export const analyzeItemConversion = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  businessRules: IBusinessRule[]
): IItemConversionInfo => {
  // For Brand Menu, we use the item name for identification
  const sourceItemName = cartItem.name;

  // Find a matching business rule
  if (!businessRules || businessRules.length === 0) {
    return { shouldConvert: false };
  }

  if (!modifierGroupName) {
    return { shouldConvert: false };
  }

  const matchingALaCarteConversion = findMatchingALaCarteConversionRule(
    sourceItemName,
    modifierGroupName,
    selectedMenuItemName,
    businessRules
  );

  if (matchingALaCarteConversion) {
    const targetDisplay = matchingALaCarteConversion.then.target.join(', ');
    logger.log(
      `Item conversion triggered via business rule (combo -> a la carte): ${cartItem.name} -> ${targetDisplay}`
    );

    const targetItemId = matchingALaCarteConversion.then.target[0];

    const optionsToPreSelect =
      matchingALaCarteConversion.then.target_options.length > 0
        ? matchingALaCarteConversion.then.target_options
        : [];

    return {
      shouldConvert: true,
      sourceItemId: sourceItemName,
      targetItemId,
      originalCartItemId: cartItem.cartItemId,
      isReverseConversion: true,
      optionsToPreSelect,
    };
  }

  const matchingComboConversion = findMatchingComboConversionRule(
    sourceItemName,
    modifierGroupName,
    selectedMenuItemName,
    businessRules
  );

  if (!matchingComboConversion) return { shouldConvert: false };

  // Extract the conversion size from the selected option for pre-selection
  const conversionSize = getConversionSizeFromSelection(selectedMenuItemName);

  const targetDisplay = matchingComboConversion.then.target.join(', ');
  logger.log(
    `Item conversion triggered via business rule: ${cartItem.name} -> ${targetDisplay}`
  );

  // Build options to pre-select based on business rule
  // The target_options could specify which combo size to pre-select, but for now we use the selected size
  // Options will be found dynamically by searching the target item's constraints
  const optionsToPreSelect = conversionSize ? [`${conversionSize} combo`] : [];

  // For combo conversion, target should be a string (item name)
  const targetItemId = matchingComboConversion.then.target[0];

  return {
    shouldConvert: true,
    sourceItemId: sourceItemName,
    targetItemId,
    conversionSize: conversionSize || undefined,
    originalCartItemId: cartItem.cartItemId,
    isReverseConversion: false,
    optionsToPreSelect,
  };
};
